// App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import CustomToast from './components/common/CustomToast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TournamentProvider } from './context/TournamentContext';
import { TeamProvider } from './context/TeamContext';
import { SearchProvider } from './context/SearchContext';
import { LanguageProvider } from './context/LanguageContext';
import { ChatProvider } from './context/ChatContext';
import { DMProvider } from './context/DMContext';
import { ChatHubProvider } from './context/ChatHubContext';
import { useTranslation } from 'react-i18next';
import './i18n'; // Initialize i18n
import useEasterEgg from './hooks/useEasterEgg';

// Critical pages - loaded synchronously for fast initial experience
import Home from './pages/Home';
import Layout from './components/layout/Layout';
import PageTransition from './components/common/PageTransition';
import PageLoader from './components/common/PageLoader';
import {LeaderboardProvider} from "./context/LeaderboardContext";

// Heavy pages - lazy loaded to reduce initial bundle size
const Leaderboards = lazy(() => import('./pages/Leaderboards'));
const PlayerStats = lazy(() => import('./pages/PlayerStats'));
const TournamentDetails = lazy(() => import('./pages/TournamentDetails'));
const TeamDetails = lazy(() => import('./pages/TeamDetails'));
const CreateTournament = lazy(() => import('./pages/CreateTournament'));
const Teams = lazy(() => import('./pages/Teams'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Profile = lazy(() => import('./pages/Profile'));
const Players = lazy(() => import('./pages/Players'));
const MatchDetails = lazy(() => import('./pages/MatchDetails'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Calendar = lazy(() => import('./pages/Calendar'));

// LFS Pages
const LFSSettings = lazy(() => import('./pages/LFS/Settings'));
const LFSDiscover = lazy(() => import('./pages/LFS/Discover'));
const LFSSchedule = lazy(() => import('./pages/LFS/Schedule'));
const LFSRequests = lazy(() => import('./pages/LFS/Requests'));
const LFSScrims = lazy(() => import('./pages/LFS/Scrims'));
const LFSScrimChat = lazy(() => import('./pages/LFS/ScrimChat'));

// Loading Component
const LoadingScreen = () => {
  const { t } = useTranslation();
  return <PageLoader fullScreen size="lg" label={t('common.loading')} />;
};

// Lightweight loading component for lazy-loaded routes
const LazyLoadFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <PageLoader />
  </div>
);

// Wrapper for lazy-loaded routes with Suspense
const LazyRoute = ({ children }) => (
  <Suspense fallback={<LazyLoadFallback />}>
    {children}
  </Suspense>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, error } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#191c23', backgroundImage: 'radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)', backgroundSize: '22px 22px' }}>
          <div className="text-center bg-[#1e2129] border border-white/10 rounded-2xl p-8 shadow-lg">
            <div className="text-white text-xl font-semibold mb-4">{t('auth.authenticationError')}</div>
            <div className="text-red-300 mb-4">{error}</div>
            <button
                onClick={() => window.location.href = '/'}
                className="bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              {t('auth.tryAgain')}
            </button>
          </div>
        </div>
    );
  }

  return user ? children : <Navigate to="/" replace />;
};

// Admin-only Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, error } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#191c23', backgroundImage: 'radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)', backgroundSize: '22px 22px' }}>
          <div className="text-center bg-[#1e2129] border border-white/10 rounded-2xl p-8 shadow-lg">
            <div className="text-white text-xl font-semibold mb-4">{t('auth.authenticationError')}</div>
            <div className="text-red-300 mb-4">{error}</div>
            <button
                onClick={() => window.location.href = '/'}
                className="bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              {t('auth.tryAgain')}
            </button>
          </div>
        </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppContent() {
  const { loading } = useAuth();
  useEasterEgg(['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight'], () => {
    window.open('https://www.youtube.com/watch?v=ur-ggo4Aek8', '_blank');
  });

  useEasterEgg('rodrigosilva'.split(''), () => {
    window.open('https://www.youtube.com/watch?v=IzRNxhkE2I8', '_blank');
  });

  if (loading) {
    return <LoadingScreen />;
  }

  return (
      <Router>
          <Routes>
            {/* Single homepage for everyone */}
            <Route path="/" element={
              <Layout>
                <PageTransition>
                  <Home />
                </PageTransition>
              </Layout>
            } />
            {/* Legacy redirects */}
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Navigate to="/" replace />} />

            {/* Routes accessible to all users */}
            <Route path="/tournaments" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <Tournaments />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/tournaments/:tournamentId" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <TournamentDetails />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            {/* Redirect old past-tournaments route to completed tab */}
            <Route path="/past-tournaments" element={<Navigate to="/tournaments?tab=completed" replace />} />
            <Route path="/teams" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <Teams />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/leaderboards" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <Leaderboards />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/players" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <Players />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/stats/:playerId" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <PlayerStats />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            {/* Protected Routes */}
            <Route path="/calendar" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <Calendar />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/tournaments/create" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <CreateTournament />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/teams/:teamId" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <TeamDetails />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />

            <Route path="/match/:matchId" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <MatchDetails />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <UserProfile />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/profile/edit" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <Profile />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/profile/:userId" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <UserProfile />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />


            {/* LFS (Looking For Scrim) Routes */}
            <Route path="/lfs" element={<Navigate to="/lfs/schedule" replace />} />
            <Route path="/lfs/settings" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <LFSSettings />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/lfs/discover" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <LFSDiscover />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/lfs/schedule" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <LFSSchedule />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/lfs/requests" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <LFSRequests />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/lfs/scrims" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <LFSScrims />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/lfs/scrims/:scrimId/chat" element={
              <ProtectedRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <LFSScrimChat />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </ProtectedRoute>
            } />

            {/* Admin Panel Route */}
            <Route path="/admin" element={
              <AdminRoute>
                <Layout>
                  <PageTransition>
                    <LazyRoute>
                      <AdminPanel />
                    </LazyRoute>
                  </PageTransition>
                </Layout>
              </AdminRoute>
            } />

            {/* Legacy redirect */}
            <Route path="/scrim-matches" element={<Navigate to="/lfs/scrims" replace />} />

            {/* Legal Pages */}
            <Route path="/terms" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <TermsOfService />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            <Route path="/privacy" element={
              <Layout>
                <PageTransition>
                  <LazyRoute>
                    <PrivacyPolicy />
                  </LazyRoute>
                </PageTransition>
              </Layout>
            } />
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Toast notifications — custom teal/dark bar with close button + auto-dismiss progress bar */}
          <Toaster
              position="top-right"
              gutter={10}
              toastOptions={{
                duration: 3500,
                error: { duration: 5000 },
                style: { background: 'transparent', boxShadow: 'none', padding: 0, margin: 0 },
              }}
          >
              {(t) => <CustomToast t={t} />}
          </Toaster>
      </Router>
  );
}

function App() {

  return (
      <LanguageProvider>
        <AuthProvider>
          <TournamentProvider>
            <TeamProvider>
              <LeaderboardProvider>
                <SearchProvider>
                  <ChatProvider>
                    <DMProvider>
                      <ChatHubProvider>
                        <AppContent />
                      </ChatHubProvider>
                    </DMProvider>
                  </ChatProvider>
                </SearchProvider>
              </LeaderboardProvider>
            </TeamProvider>
          </TournamentProvider>
        </AuthProvider>
      </LanguageProvider>
  );
}

export default App;
